/**
 * BillingChrome — the app-level mount point for billing surfaces. Wraps the
 * authed shell with:
 *
 *   1. ``UnlockModal`` — opened by the notice layer's rung-3 CTA OR by a
 *      gated entry point calling ``requireUnlocked(reason, action)``.
 *   2. ``ManageSubscriptionModal`` — opened by the notice layer's CTA for
 *      every state whose action is billing detail (add a card, resume, update
 *      a card), and programmatically from Settings.
 *
 * Also exposes the ``useRequireUnlocked()`` hook so any gated creation entry
 * point (project create, remix, avatar generate) can defer its action behind
 * the upsell modal when ``can_create`` is false. Backs launch-compliance R8.3.
 *
 * Kids never see billing surfaces, never trigger an UnlockModal — the gate is
 * a no-op for ``kind === "kid"``. ``resolveNotice`` enforces the same rule.
 *
 * This component NO LONGER RENDERS THE STRIP. It used to mount one at
 * ``position:fixed; top:0; zIndex:80``, which collided with the 56px top bar
 * in both shells (covering it in the navigation view, hiding behind it in the
 * project view). The notice row is now an ordinary flex child that each shell
 * lays out above its own top bar — see ``NoticeRow``. What stays here is the
 * modal ownership and the gate.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { UnlockModal, type UnlockReason } from "./UnlockModal";
import { ManageSubscriptionModal } from "./ManageSubscriptionModal";
import { useBilling } from "./useBilling";
import { billingPlatform } from "./platform";
import { useErasure } from "../erasure/useErasure";
import type { AccountNotice } from "./accountNotice";
import { SeatCard } from "./SeatCard";
import { MailingAddressPrompt } from "./MailingAddressPrompt";
import { SEAT_PROMPT_KEY } from "./seats";
import { CREATION_LOCKED_EVENT, SEAT_NOT_HELD_EVENT } from "../api";
import { useIsChildSession } from "../hooks/useIsChildSession";

interface BillingChromeApi {
  /**
   * Run ``action`` immediately when the tenant can create; otherwise open the
   * UnlockModal with ``reason``. After a successful subscribe the modal closes
   * and ``action`` runs once.
   *
   * ``onNotRun``, when given, is called once if ``action`` will not run after all: the
   * sheet closed without a subscribe (its skip included), the seat card closed, or a web
   * subscribe left for /subscribed (which drops the action). A caller that must not lose
   * what was asked for keeps it there (the share page's remix saves the project instead,
   * game-on-freeform U12). It may return a promise; the /subscribed hop waits for it.
   *
   * ``resumeTo``, when given, is a same-site URL that asks for ``action`` again. A web
   * subscribe then keeps it: /subscribed's Continue goes there with the plan live, and
   * ``onNotRun`` does not run for the hop (the share page's remix and Make your own).
   */
  requireUnlocked: (
    reason: UnlockReason,
    action: () => void | Promise<void>,
    onNotRun?: () => void | Promise<void>,
    resumeTo?: string,
  ) => void;
  /** Open the Manage Subscription modal (programmatic, e.g. from Settings). */
  openManage: () => void;
  /** Open the Unlock takeover directly (the notice layer's rung-3 CTA). */
  openUnlock: (reason: UnlockReason) => void;
}

const BillingChromeCtx = createContext<BillingChromeApi | null>(null);

/**
 * Returns the ``requireUnlocked``/``openManage`` API. Outside a
 * ``BillingChrome`` (tests, unauth screens) this falls open — the action runs
 * unconditionally and ``openManage`` is a no-op — so call sites never need a
 * provider-presence check.
 */
export function useRequireUnlocked(): BillingChromeApi {
  const ctx = useContext(BillingChromeCtx);
  if (ctx) return ctx;
  return {
    requireUnlocked: (_reason, action) => {
      void action();
    },
    openManage: () => {},
    openUnlock: () => {},
  };
}

export interface BillingChromeProps {
  children: React.ReactNode;
  /** Number of active kid profiles, threaded through to ManageSubscriptionModal. */
  kidCount?: number;
}

export function BillingChrome({ children, kidCount }: BillingChromeProps): JSX.Element {
  const b = useBilling();
  // savesForLater: the gated action has a fallback that keeps the project (U12), and the
  // sheet's "Keep playing free" says so (design-trial-sheet.md).
  const [unlock, setUnlock] = useState<{ open: boolean; reason: UnlockReason; savesForLater?: boolean }>({
    open: false,
    reason: "locked",
  });
  const [manageOpen, setManageOpen] = useState(false);
  const pendingAction = useRef<(() => void | Promise<void>) | null>(null);
  const pendingNotRun = useRef<(() => void | Promise<void>) | null>(null);
  const pendingResume = useRef<string | null>(null);
  /** The action will not run: hand over to its caller's fallback, once. */
  const notRun = useCallback((): Promise<void> => {
    const fallback = pendingNotRun.current;
    pendingNotRun.current = null;
    return Promise.resolve(fallback?.()).catch(() => {});
  }, []);
  // Seat unit: a member the plan does not cover gets this card, not the paywall.
  const [seatCardOpen, setSeatCardOpen] = useState(false);
  const isChild = useIsChildSession();
  useEffect(() => {
    // The server's 403 `seat_not_held` (voice start, a new project) lands here.
    const open = () => setSeatCardOpen(true);
    window.addEventListener(SEAT_NOT_HELD_EVENT, open);
    return () => window.removeEventListener(SEAT_NOT_HELD_EVENT, open);
  }, []);

  // An action asked for before /billing/state has loaded waits here; deciding on
  // a state we do not have yet ran a paid avatar draw for a free family's child
  // (CastingStudio draws on mount), and would flash a paying grown-up the paywall.
  const waitingForBilling = useRef<{
    reason: UnlockReason;
    action: () => void | Promise<void>;
    onNotRun?: () => void | Promise<void>;
    resumeTo?: string;
  } | null>(null);

  const requireUnlocked = useCallback<BillingChromeApi["requireUnlocked"]>(
    (reason, action, onNotRun, resumeTo) => {
      if (b.isReady === false) {
        waitingForBilling.current = { reason, action, onNotRun, resumeTo };
        return;
      }
      // Kids aren't the billable principal; their seat is covered by the
      // family subscription. Run unconditionally so kid flows don't trip the
      // upsell.
      if (b.kind === "kid") {
        void action();
        return;
      }
      // A child in a grown-up's family carries the family's billing state
      // (kind "individual"), so the kind alone let a 7-year-old meet the
      // paywall. Decide on the session's role: a child never sees a price,
      // only "Ask a grown-up" (operator rule; first-ten-minutes walk 2026-10-22).
      if (isChild) {
        if (b.can_create) void action();
        else {
          pendingNotRun.current = onNotRun ?? null;
          setSeatCardOpen(true);
        }
        return;
      }
      // A live plan whose seats are with other members: the "ask a grown-up"
      // card, never the paywall (seat unit, 2026-10-22). Checked before
      // ``can_create``, which is false for an unseated member too.
      if (!b.locked && b.seated === false) {
        pendingNotRun.current = onNotRun ?? null;
        setSeatCardOpen(true);
        return;
      }
      // Server-authoritative ``can_create`` — true for trialing/active,
      // false for free/locked.
      if (b.can_create) {
        void action();
        return;
      }
      pendingAction.current = action;
      pendingNotRun.current = onNotRun ?? null;
      pendingResume.current = resumeTo ?? null;
      setUnlock({ open: true, reason, savesForLater: !!onNotRun });
    },
    [b.kind, b.can_create, b.locked, b.seated, b.isReady, isChild],
  );

  useEffect(() => {
    if (b.isReady === false || !waitingForBilling.current) return;
    const { reason, action, onNotRun, resumeTo } = waitingForBilling.current;
    waitingForBilling.current = null;
    requireUnlocked(reason, action, onNotRun, resumeTo);
  }, [b.isReady, requireUnlocked]);

  const openManage = useCallback(() => setManageOpen(true), []);

  // A downgrade on Apple's sheet left more members than seats: on the grown-up's
  // next visit, open Plan & billing on the picker — once per visit (this tab),
  // and reachable again from Settings' Plan & billing row. Kids get `seats: null`.
  const choicePending = !!b.seats?.choice_pending;
  useEffect(() => {
    if (!choicePending) return;
    try {
      if (sessionStorage.getItem(SEAT_PROMPT_KEY)) return;
      sessionStorage.setItem(SEAT_PROMPT_KEY, "1");
    } catch { /* storage blocked: prompt anyway */ }
    setManageOpen(true);
  }, [choicePending]);

  const handleUnlockClosed = useCallback(() => {
    // A subscribe already took the action (handleSubscribed clears both first).
    if (pendingAction.current) void notRun();
    pendingAction.current = null;
    pendingNotRun.current = null;
    pendingResume.current = null;
    setUnlock((prev) => ({ ...prev, open: false }));
  }, [notRun]);

  const handleSubscribed = useCallback(() => {
    // Drop the pending gated action and hard-nav to /subscribed so the Google
    // Ads conversion can fire on a real URL hit. The SubscribedPage's
    // "Continue" CTA returns the user to where they were. UX tradeoff: we
    // surface a celebratory landing instead of silently retrying the gated
    // action — worth it for paid-acquisition tracking.
    setUnlock((prev) => ({ ...prev, open: false }));
    const resumeTo = pendingResume.current;
    pendingResume.current = null;
    // In the iPad app (the store takes the money) there is no ads page to
    // land on, and a hard navigation would reload the whole web view: close
    // and carry on with what the family was doing.
    if (!billingPlatform().manages) {
      const action = pendingAction.current;
      pendingAction.current = null;
      pendingNotRun.current = null;
      if (action) void action();
      return;
    }
    // A caller that can ask again goes back there: its fallback would navigate too, and race.
    if (resumeTo) {
      pendingAction.current = null;
      pendingNotRun.current = null;
      window.location.assign(`/subscribed?return_to=${encodeURIComponent(resumeTo)}`);
      return;
    }
    // Otherwise the hop drops the action, so its caller's fallback (if any) runs first.
    const dropped = pendingAction.current !== null && pendingNotRun.current !== null;
    pendingAction.current = null;
    const current = window.location.pathname + window.location.search;
    const target = `/subscribed?return_to=${encodeURIComponent(current)}`;
    if (dropped) void notRun().then(() => window.location.assign(target));
    else {
      pendingNotRun.current = null;
      window.location.assign(target);
    }
  }, [notRun]);

  const openUnlock = useCallback(
    (reason: UnlockReason) => {
      if (isChild) setSeatCardOpen(true);
      else setUnlock({ open: true, reason });
    },
    [isChild],
  );
  useEffect(() => {
    // The server's 403 `creation_locked` (a voice start on a free or lapsed
    // plan) lands here: the paywall for a grown-up, the seat card for a child.
    const open = () => openUnlock("create");
    window.addEventListener(CREATION_LOCKED_EVENT, open);
    return () => window.removeEventListener(CREATION_LOCKED_EVENT, open);
  }, [openUnlock]);

  return (
    <BillingChromeCtx.Provider value={{ requireUnlocked, openManage, openUnlock }}>
      {children}
      <SeatCard
        open={seatCardOpen}
        audience={isChild ? "kid" : "grownup"}
        onClose={() => {
          setSeatCardOpen(false);
          void notRun();
        }}
        onChooseSeats={openManage}
      />
      <UnlockModal
        open={unlock.open}
        reason={unlock.reason}
        onClose={handleUnlockClosed}
        onSubscribed={handleSubscribed}
        savesForLater={unlock.savesForLater}
      />
      <MailingAddressPrompt />
      <ManageSubscriptionModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        kidCount={kidCount}
        onUnlock={(reason) => {
          setManageOpen(false);
          setUnlock({ open: true, reason });
        }}
      />
    </BillingChromeCtx.Provider>
  );
}

/**
 * Maps a notice's ``action`` onto the thing that actually happens. Kept here
 * because BillingChrome owns the modals; ``accountNotice.ts`` stays pure.
 *
 * ``keep_account`` is the deletion-grace CTA and calls off the erasure
 * directly — it has no modal.
 */
export function useNoticeAction(): (notice: AccountNotice | null) => void {
  const { openManage, openUnlock } = useRequireUnlocked();
  const erasure = useErasure();
  return useCallback(
    (notice: AccountNotice | null) => {
      if (!notice) return;
      switch (notice.action) {
        case "manage":
          openManage();
          return;
        case "unlock":
          openUnlock("locked");
          return;
        case "keep_account":
          void erasure.cancel().catch(() => {
            // A 410 means the window already elapsed; the notice will clear
            // itself on the next refresh either way.
          });
          return;
        case "none":
          return;
      }
    },
    [openManage, openUnlock, erasure],
  );
}
